import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Star, ExternalLink, Flame, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/lib/router-compat";
import { SaleRibbon } from "@/components/SaleRibbon";
import { getSaleInfo, formatSaleEnds } from "@/lib/saleHelpers";

export type InternalProductType =
  | "lawn_mowers"
  | "generators"
  | "chainsaws"
  | "pressure_washers"
  | "impact_drivers"
  | "products";

interface Props {
  productId: string;
  productType: InternalProductType;
}

const ROUTE_PREFIX: Record<Exclude<InternalProductType, "products">, string> = {
  lawn_mowers: "/lawn-mowers",
  generators: "/generators",
  chainsaws: "/chainsaws",
  pressure_washers: "/pressure-washers",
  impact_drivers: "/impact-drivers",
};

export default function InternalProductCard({ productId, productType }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["internal-product-card", productType, productId],
    enabled: !!productId && !!productType,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const baseCols = "id, brand, model, slug, image_url, price_gbp, previous_price_gbp, price_drop_at, sale_end_date, rating, affiliate_link, best_for";
      const select = productType === "products" ? `${baseCols}, product_type` : baseCols;
      const { data, error } = await supabase
        .from(productType as any)
        .select(select)
        .eq("id", productId)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  if (isLoading) {
    return (
      <div className="not-prose my-6 rounded-lg border border-border bg-card p-4 flex gap-4 items-center">
        <Skeleton className="h-24 w-24 rounded" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-9 w-32" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="not-prose my-6 rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        Product unavailable.
      </div>
    );
  }

  const title = [data.brand, data.model].filter(Boolean).join(" ") || "Product";
  const internalPath =
    productType === "products"
      ? `/${data.product_type || ""}/${data.slug}`
      : `${ROUTE_PREFIX[productType as Exclude<InternalProductType, "products">]}/${data.slug}`;

  const affiliate = data.affiliate_link as string | undefined;

  const ImageWrap = ({ children }: { children: React.ReactNode }) =>
    affiliate ? (
      <a href={affiliate} target="_blank" rel="noopener noreferrer sponsored" className="shrink-0 mx-auto sm:mx-0 block relative">
        {children}
      </a>
    ) : (
      <Link to={internalPath} className="shrink-0 mx-auto sm:mx-0 block relative">
        {children}
      </Link>
    );

  const TitleWrap = ({ children }: { children: React.ReactNode }) =>
    affiliate ? (
      <a
        href={affiliate}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="font-semibold text-foreground hover:text-primary leading-snug line-clamp-3"
      >
        {children}
      </a>
    ) : (
      <Link
        to={internalPath}
        className="font-semibold text-foreground hover:text-primary leading-snug line-clamp-3"
      >
        {children}
      </Link>
    );

  return (
    <aside
      className="not-prose my-6 rounded-xl border border-border bg-card shadow-soft overflow-hidden"
      aria-label={`Product: ${title}`}
    >
      <div className="flex flex-col sm:flex-row gap-4 p-4">
        {data.image_url && (
          <ImageWrap>
            <SaleRibbon
              price={data.price_gbp}
              previousPrice={data.previous_price_gbp}
              priceDropAt={data.price_drop_at}
              saleEndDate={data.sale_end_date}
            />
            <img
              src={data.image_url}
              alt={title}
              loading="lazy"
              width={128}
              height={128}
              style={{ width: 128, height: 128, objectFit: "contain" }}
              className="rounded bg-white"
            />
          </ImageWrap>
        )}


        <div className="flex-1 min-w-0 flex flex-col">
          {data.brand && (
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {data.brand}
            </div>
          )}
          <TitleWrap>{title}</TitleWrap>

          {data.rating && (
            <div className="flex items-center gap-1.5 mt-2 text-sm">
              <span className="flex items-center gap-0.5">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{Number(data.rating).toFixed(1)}</span>
              </span>
            </div>
          )}

          {data.best_for && (
            <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
              Best for: {data.best_for}
            </div>
          )}

          <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
            <div>
              {data.price_gbp && (() => {
                const sale = getSaleInfo({
                  price: data.price_gbp,
                  previousPrice: data.previous_price_gbp,
                  priceDropAt: data.price_drop_at,
                  saleEndDate: data.sale_end_date,
                });
                return (
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-2xl font-bold text-foreground leading-none">
                      £{Number(data.price_gbp).toFixed(2)}
                    </span>
                    {sale.isOnSale && (
                      <>
                        <span className="text-sm text-muted-foreground line-through">
                          £{Number(data.previous_price_gbp).toFixed(2)}
                        </span>
                        {sale.percentOff !== null && (
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded text-white"
                            style={{ backgroundColor: "#dc2626" }}
                          >
                            -{sale.percentOff}% (save £{sale.amountOff?.toFixed(2)})
                          </span>
                        )}
                        {sale.justDropped && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded text-white" style={{ backgroundColor: "#16a34a" }}>
                            <Flame className="h-3 w-3" aria-hidden /> Just dropped
                          </span>
                        )}
                        {sale.saleEndsSoon && sale.saleEndsAt && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-900">
                            <Clock className="h-3 w-3" aria-hidden /> {formatSaleEnds(sale.saleEndsAt)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="flex gap-2">

              {affiliate ? (
                <a
                  href={affiliate}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm"
                  style={{ backgroundColor: "#16a34a" }}
                >
                  Check price
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <Link
                  to={internalPath}
                  className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm"
                  style={{ backgroundColor: "#16a34a" }}
                >
                  Read review
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
